/*
 * CART DRAWER UPGRADES — assets/cart-upgrades.js (2026-08)
 *
 * Three jobs, all built as custom elements because Impact's CartDrawer REPLACES its entire
 * DOM subtree on cart:change / cart:refresh. A one-shot DOMContentLoaded script would bind
 * once and then be silently thrown away on the first cart mutation; custom elements
 * re-upgrade automatically every time the markup comes back.
 *
 *   <cart-protection>  optional paid protection line, added/removed via the cart AJAX API
 *   <dispatch-eta>     appends the shopper's region to an already-complete sentence
 *
 * Plus a history-navigation repair: Impact only heals the drawer on a bfcache hit and the
 * /cart page never heals at all, so a shopper who edits their cart AT checkout and presses
 * Back sees stale contents while the header badge shows the truth.
 *
 * No jQuery. No build step. Nothing here runs on a page without a cart drawer.
 */

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

async function readCart() {
  try {
    const res = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } });
    const text = await res.text();
    return JSON.parse(text);
  } catch (e) {
    return null;
  }
}

const refreshCart = () => document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

/* ============================================================
   0. COLLAPSIBLE BLOCKS
   The kit nudge and the protection row each collapse to one line. State has to be
   re-applied after every render, not just once: CartDrawer replaces its whole subtree on
   cart:change, so a block the shopper collapsed would spring back open the moment they
   changed a quantity. Hence the custom-element entry points below rather than a one-shot
   pass at load.
   ============================================================ */
const COLLAPSE_STORE = 'bossmanCartCollapse';

function readCollapse() {
  try { return JSON.parse(localStorage.getItem(COLLAPSE_STORE) || '{}'); } catch (e) { return {}; }
}

function setCollapsed(host, open, persist) {
  host.dataset.open = open ? '1' : '0';
  const btn = host.querySelector('[data-collapse-toggle]');
  if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (!persist) return;
  try {
    const state = readCollapse();
    state[host.dataset.collapseKey] = open ? 1 : 0;
    localStorage.setItem(COLLAPSE_STORE, JSON.stringify(state));
  } catch (e) { /* private mode: the block still toggles, it just will not be remembered */ }
}

function applyCollapse(host) {
  const key = host.dataset.collapseKey;
  if (!key) return;
  const stored = readCollapse()[key];
  const open = stored === undefined ? host.dataset.collapseDefault === '1' : stored === 1;
  setCollapsed(host, open, false);
  // transitions are enabled a frame later, so a block restored as collapsed does not
  // visibly animate shut every time the drawer re-renders
  requestAnimationFrame(() => { host.dataset.collapseReady = '1'; });
}

document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-collapse-toggle]');
  if (!btn) return;
  const host = btn.closest('[data-collapse-key]');
  if (!host) return;
  event.preventDefault();
  setCollapsed(host, host.dataset.open !== '1', true);
});

class CuCollapse extends HTMLElement {
  connectedCallback() { applyCollapse(this); }
}
if (!customElements.get('cu-collapse')) customElements.define('cu-collapse', CuCollapse);

/* ============================================================
   1. PACKAGE PROTECTION
   ============================================================ */
class CartProtection extends HTMLElement {
  connectedCallback() {
    applyCollapse(this);
    this.input = this.querySelector('.cart-protect__input');
    this.spinner = this.querySelector('.cart-protect__spinner');
    if (!this.input || this.dataset.bound) return;
    this.dataset.bound = '1';
    this.input.addEventListener('change', () => this.toggle());
  }

  busy(on) {
    this.classList.toggle('is-busy', on);
    if (this.spinner) this.spinner.hidden = !on;
    if (this.input) this.input.disabled = on;
  }

  async toggle() {
    const wantsOn = this.input.checked;
    const variantId = Number(this.dataset.variantId);
    const lineKey = this.dataset.lineKey;
    if (!variantId) return;
    this.busy(true);

    try {
      let res;
      if (wantsOn) {
        res = await fetch(ROOT + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: 1,
              // underscore prefix keeps it out of the customer-facing properties display
              properties: { _protection: '1' }
            }]
          })
        });
      } else {
        // remove by line key when we have it (survives duplicate variants), else by id
        const body = lineKey ? { id: lineKey, quantity: 0 } : { id: variantId, quantity: 0 };
        res = await fetch(ROOT + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body)
        });
      }

      if (!res.ok) {
        // put the checkbox back where the cart actually is, then surface the reason
        this.input.checked = !wantsOn;
        let msg = '';
        try {
          const j = await res.json();
          msg = j.description || j.message || '';
        } catch (e) { /* html error page */ }
        document.dispatchEvent(new CustomEvent('cart:error', {
          bubbles: true,
          detail: { error: msg || 'Could not update package protection. Please try again.' }
        }));
        this.busy(false);
        return;
      }

      // the drawer re-renders from cart truth, which re-derives the checkbox state
      refreshCart();
    } catch (e) {
      this.input.checked = !wantsOn;
      this.busy(false);
      return;
    }
    // deliberately leave busy on: this element is about to be replaced by the re-render
  }
}
if (!customElements.get('cart-protection')) customElements.define('cart-protection', CartProtection);

/* ------------------------------------------------------------
   Protection is a fee on an order, so it must never BE the order. If the shopper turns it
   on and then removes their last real product, Shopify still reports item_count 1: the
   drawer renders its populated layout with a blank product list, a non-zero total and a
   live Checkout button, and the shopper can buy nothing but a $4.99 fee. Drop the line
   instead. Removing it takes the cart to empty, so this cannot recurse.
   ------------------------------------------------------------ */
async function dropOrphanProtection(cart) {
  if (!cart || cart.items.length !== 1) return false;
  const el = document.querySelector('cart-protection[data-variant-id]');
  if (!el) return false;
  const variantId = Number(el.dataset.variantId);
  const only = cart.items[0];
  if (!variantId || Number(only.variant_id) !== variantId) return false;
  try {
    const res = await fetch(ROOT + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: only.key, quantity: 0 })
    });
    if (!res.ok) return false;
  } catch (e) {
    return false;
  }
  refreshCart();
  return true;
}

/* ============================================================
   1b. "COMPLETE YOUR KIT" RAIL
   Delegated on document because the drawer replaces its subtree on every cart change,
   so a listener bound to the buttons themselves would be discarded on the first add.
   ============================================================ */
// Keep a card's price and photo honest when its variant changes. Delegated for the same reason
// as the add handler: the drawer throws this markup away on every cart change.
document.addEventListener('change', (event) => {
  const picker = event.target.closest('[data-cu-rail-variant]');
  if (!picker) return;
  const card = picker.closest('.cu-rail__item');
  const opt = picker.selectedOptions[0];
  if (!card || !opt) return;

  // Photo follows the picker (2026-09-14). Each option carries the variant's own image as
  // data-image / data-srcset (snippets/cart-kit-rail.liquid); an option without one means the
  // variants share a photo (the bar soap), so the card returns to the image it rendered with.
  const img = card.querySelector('[data-cu-rail-img]');
  if (img) {
    if (img.dataset.defaultSrc === undefined) {
      img.dataset.defaultSrc = img.getAttribute('src') || '';
      img.dataset.defaultSrcset = img.getAttribute('srcset') || '';
    }
    const nextSrc = opt.dataset.image || img.dataset.defaultSrc;
    const nextSrcset = opt.dataset.image ? (opt.dataset.srcset || '') : img.dataset.defaultSrcset;
    if (nextSrc && img.getAttribute('src') !== nextSrc) {
      // srcset first: a browser that re-selects from srcset on src change would otherwise
      // briefly paint the old candidate
      if (nextSrcset) img.setAttribute('srcset', nextSrcset); else img.removeAttribute('srcset');
      img.setAttribute('src', nextSrc);
    }
  }

  const target = card.querySelector('[data-cu-rail-price]');
  if (!target || !opt.dataset.price) return;

  // built as nodes rather than innerHTML: these strings come from Shopify's money filter,
  // but the price line is never worth an HTML injection surface
  target.textContent = '';
  if (opt.dataset.compare) {
    const now = document.createElement('span');
    now.className = 'cu-rail__now';
    now.textContent = opt.dataset.price;
    const was = document.createElement('s');
    was.textContent = opt.dataset.compare;
    target.append(now, ' ', was);
  } else {
    target.textContent = opt.dataset.price;
  }
});

document.addEventListener('click', async (event) => {
  const btn = event.target.closest('[data-cu-rail-add]');
  if (!btn) return;
  event.preventDefault();
  // Cards for multi-variant products carry a picker, and its current value wins over the
  // button's default. Without it the rail could only ever offer the 15 single-variant
  // products in the collection and ran dry after a handful of adds.
  const card = btn.closest('.cu-rail__item');
  const picker = card && card.querySelector('[data-cu-rail-variant]');
  // getAttribute, not dataset: the camelCase form (dataset.cuRailAdd) is invisible to a
  // find-and-replace on the attribute name, which is exactly how the kit-rail -> cu-rail
  // rename silently broke every single-variant card in this rail.
  const id = Number((picker && picker.value) || btn.getAttribute('data-cu-rail-add'));
  if (!id || btn.disabled) return;
  const label = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Adding…';
  try {
    const res = await fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id, quantity: 1 }] })
    });
    if (!res.ok) {
      let msg = '';
      try { const j = await res.json(); msg = j.description || j.message || ''; } catch (e) {}
      document.dispatchEvent(new CustomEvent('cart:error', {
        bubbles: true, detail: { error: msg || 'Could not add that item. Please try again.' }
      }));
      btn.disabled = false;
      btn.textContent = label;
      return;
    }
    // the drawer re-renders from cart truth: the item leaves the rail and the nudge advances
    refreshCart();
  } catch (e) {
    btn.disabled = false;
    btn.textContent = label;
  }
});

/* ============================================================
   2. DISPATCH ETA
   The Liquid ships a complete honest sentence; JS only ever APPENDS a region.
   Never rendered server-side: this section sits in Shopify's shared page cache, so a
   server-rendered per-visitor location would be served to other shoppers.
   ============================================================ */
const GEO_KEY = 'bossmanGeoState';

class DispatchEta extends HTMLElement {
  connectedCallback() {
    if (this.dataset.done) return;
    this.dataset.done = '1';
    this.fill();
  }

  async fill() {
    // country gate: the dispatch promise and the location wording are US-shaped
    const country = (window.Shopify && window.Shopify.country) || '';
    if (country && country !== 'US') return;

    let loc = null;
    try { loc = sessionStorage.getItem(GEO_KEY); } catch (e) { /* private mode */ }
    if (loc === 'none') return;

    if (!loc) {
      try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 2500);
        const r = await fetch('https://ipwho.is/?fields=success,country_code,region', { signal: ctrl.signal });
        clearTimeout(timer);
        const j = await r.json();
        // STATE only, deliberately. IP geolocation is dependable at state level and
        // unreliable at city level - it returns the ISP's routing city, which is often a
        // neighbouring town. A wrong city is worse than no city: it is visibly wrong to
        // the one person who can check it.
        loc = (j && j.success && j.country_code === 'US' && j.region) ? j.region : 'none';
      } catch (e) {
        loc = 'none';
      }
      try { sessionStorage.setItem(GEO_KEY, loc); } catch (e) { /* fine */ }
    }

    if (!loc || loc === 'none') return;
    const target = this.querySelector('[data-dispatch-text]');
    if (!target) return;
    const base = this.dataset.template || target.textContent;
    target.textContent = base + ' to ' + loc;
  }
}
if (!customElements.get('dispatch-eta')) customElements.define('dispatch-eta', DispatchEta);

/* ============================================================
   3. HISTORY-NAVIGATION REPAIR
   Impact's CartDrawer._onPageShow hard-returns unless event.persisted is true, and
   sections/main-cart.liquid carries no JS at all. So: edit the cart at checkout, press
   Back, and the drawer and /cart page show pre-checkout contents while the header badge
   (CartCount, which refreshes unconditionally) shows the truth.
   ============================================================ */
/* ============================================================
   CLOSE LOCK
   theme.js.liquid:1872 refuses to close the cart drawer for 2000ms after it opens:

     hide() { if (this._openedAt && (Date.now() - this._openedAt < 2000)) return; ... }

   That is a real guard - the drawer opens 50ms after an add-to-cart, and without it the
   same click can carry through onto the overlay and shut it again - but two seconds is far
   longer than the race it defends against. Open the drawer and try to leave, and the X and
   the overlay both do nothing. It reads as broken rather than protected.

   Rather than fork theme.js.liquid, which is currently byte-identical to live and worth
   keeping that way, the timestamp the guard reads is shifted back. The theme's own
   comparison is untouched; 2000ms of it now elapses 300ms after opening. The guard still
   covers the stray click, the shopper is no longer held hostage.

   Defined on the element, not its children, so it survives the subtree being replaced.
   ============================================================ */
const CLOSE_LOCK_MS = 300;

function relaxDrawerCloseLock() {
  const el = document.querySelector('#cart-drawer');
  if (!el || el._closeLockRelaxed) return;
  const shift = 2000 - CLOSE_LOCK_MS;
  let actual = 0;
  try {
    Object.defineProperty(el, '_openedAt', {
      configurable: true,
      get() { return actual ? actual - shift : 0; },
      set(v) { actual = v; }
    });
    el._closeLockRelaxed = true;
  } catch (e) { /* theme changed shape: leave its own guard alone */ }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', relaxDrawerCloseLock);
} else {
  relaxDrawerCloseLock();
}

/* ============================================================
   APP DOUBLE-TOGGLE GUARD (2026-09-14)
   The upsell app injected by script tag (ac2_lt.js, per-store config from
   d5zu2f4xvqanl.cloudfront.net) ships an Impact adapter that, when its own popup is about
   to show after an add-to-cart, closes the theme's drawer by CLICKING the header cart link:

     jQuery('[href="/cart"][aria-expanded="true"]')[0].click()      (220ms after "upsale_showed")

   Impact treats that link as a toggle, so the drawer that opened 50ms after the add is shut
   again ~450ms later. Its popup (#tdf_upsell) has been display:none in custom.css since
   before the takeover, so the shopper sees the drawer flash and vanish with nothing in its
   place. The theme's stock 2000ms guard above used to swallow that click by accident; the
   300ms guard lets it through. Reproduced on live and staging 2026-09-14 with the click's
   stack pointing into the app's eval'd adapter.

   A synthetic click (isTrusted false) on a cart-drawer control while the drawer is already
   open is a request for a state we are already in, so it is dropped before the theme's body
   delegate sees it, and its default (navigating to /cart) is cancelled the same way the
   theme's own handler would have. Real clicks, keyboard activation and every other close
   path (X, overlay, Escape) are untouched. Nothing here runs when the drawer is closed, so
   the app can still open the drawer after adds that bypass the theme's product form.
   ============================================================ */
document.addEventListener('click', (event) => {
  if (event.isTrusted) return;
  const target = event.target;
  if (!target || typeof target.closest !== 'function') return;
  const control = target.closest('[aria-controls="cart-drawer"]');
  if (!control) return;
  const drawer = document.getElementById('cart-drawer');
  if (!drawer || !drawer.hasAttribute('open')) return;
  event.preventDefault();
  event.stopImmediatePropagation();
}, true);

const HEAL_GUARD = 'bossmanCartHealed';
let lastKnownCount = null;

async function healAfterHistoryNav() {
  const cart = await readCart();
  if (!cart) return;

  // the /cart page has no client-side rendering to patch; if its markup disagrees with
  // reality, a reload is the only honest repair
  const path = window.location.pathname.replace(/\/$/, '');
  if (path.endsWith('/cart')) {
    // Count ONLY the cart page's own rows. The cart drawer is rendered on /cart as well,
    // so an unscoped querySelectorAll counts every line twice, never matches cart.items,
    // and reloads the page forever. Measured on staging: a 2-item cart produced 4 matches,
    // navigationType "reload", ~1.1s per cycle, on the highest-intent page on the store.
    const rows = Array.from(document.querySelectorAll('line-item, .cart-item, [data-line-item]'))
      .filter((el) => !el.closest('#cart-drawer'));

    // Second, independent belt: heal at most once per session. Even with the count fixed,
    // any future markup change that breaks the comparison must degrade to one wasted
    // reload, never to a loop a shopper cannot escape.
    let alreadyHealed = false;
    try { alreadyHealed = sessionStorage.getItem(HEAL_GUARD) === '1'; } catch (e) { /* private mode */ }

    if (rows.length && rows.length !== cart.items.length) {
      if (!alreadyHealed) {
        try { sessionStorage.setItem(HEAL_GUARD, '1'); } catch (e) { /* private mode */ }
        window.location.reload();
        return;
      }
    } else if (rows.length === cart.items.length) {
      // page agrees with the cart, so a genuinely stale page later may heal once again
      try { sessionStorage.removeItem(HEAL_GUARD); } catch (e) { /* private mode */ }
    }
  }

  // the drawer: refresh only when it is genuinely out of date, so we never fight the
  // theme's own bfcache handler or fire a pointless section fetch
  if (lastKnownCount === null || lastKnownCount !== cart.item_count) refreshCart();
  lastKnownCount = cart.item_count;
}

window.addEventListener('pageshow', () => {
  // persisted true  = bfcache restore (theme heals the drawer, but never /cart)
  // persisted false = history nav served from the HTTP cache: nothing heals it today
  healAfterHistoryNav();
});

document.addEventListener('cart:change', (event) => {
  const cart = event.detail && event.detail.cart;
  if (cart && typeof cart.item_count === 'number') lastKnownCount = cart.item_count;
  if (cart) dropOrphanProtection(cart);
});

readCart().then((c) => { if (c) lastKnownCount = c.item_count; });
